import { cn } from "../lib/cn.js";

type StatusTone =
  | "active"
  | "suspended"
  | "archived"
  | "connected"
  | "disconnected"
  | "pending"
  | "disabled"
  // Phase F · RPT status tones（非 bug 不红色：rejected 用 amber）
  | "rpt-pending"
  | "rpt-analyzing"
  | "rpt-awaiting-confirm"
  | "rpt-confirmed"
  | "rpt-rejected"
  // Phase G · 飞书长连接状态（SDK WSConnectionState 透传 + UI 友好标签）
  | "longconn-idle"
  | "longconn-connecting"
  | "longconn-connected"
  | "longconn-reconnecting"
  | "longconn-failed";

const TONE_CLASS: Record<StatusTone, string> = {
  // 非 bug 不红色：suspended / disconnected / disabled 用 amber，archived 用中性灰
  active:       "bg-emerald-400/15 text-emerald-700 border-emerald-400/40",
  connected:    "bg-emerald-400/15 text-emerald-700 border-emerald-400/40",
  suspended:    "bg-amber-500/15  text-amber-700   border-amber-500/40",
  disconnected: "bg-amber-500/15  text-amber-700   border-amber-500/40",
  pending:      "bg-amber-500/15  text-amber-700   border-amber-500/40",
  disabled:     "bg-amber-500/15  text-amber-700   border-amber-500/40",
  archived:     "bg-dosia-bg-lighter text-dosia-text-secondary border-dosia-border",
  // Phase F
  "rpt-pending":          "bg-dosia-bg-lighter text-dosia-text-secondary border-dosia-border",
  "rpt-analyzing":        "bg-dosia-accent/10 text-dosia-text border-dosia-accent/40",
  "rpt-awaiting-confirm": "bg-amber-500/20 text-amber-800 border-amber-500/40",
  "rpt-confirmed":        "bg-emerald-400/15 text-emerald-700 border-emerald-400/40",
  "rpt-rejected":         "bg-amber-500/15 text-amber-700 border-amber-500/40",
  // Phase G — 长连接：connected 绿，其余 amber/中性，failed 也走 amber（不上红，按 CLAUDE.md「非 bug 不红色」）
  "longconn-idle":         "bg-dosia-bg-lighter text-dosia-text-secondary border-dosia-border",
  "longconn-connecting":   "bg-amber-500/15 text-amber-700 border-amber-500/40",
  "longconn-connected":    "bg-emerald-400/15 text-emerald-700 border-emerald-400/40",
  "longconn-reconnecting": "bg-amber-500/20 text-amber-800 border-amber-500/40",
  "longconn-failed":       "bg-amber-500/25 text-amber-900 border-amber-500/50",
};

const TONE_LABEL: Record<StatusTone, string> = {
  active: "正常",
  suspended: "已暂停",
  archived: "已归档",
  connected: "已连接",
  disconnected: "未连接",
  pending: "待处理",
  disabled: "已禁用",
  "rpt-pending": "排队中",
  "rpt-analyzing": "分析中",
  "rpt-awaiting-confirm": "待确认",
  "rpt-confirmed": "已应用",
  "rpt-rejected": "已拒绝",
  "longconn-idle": "未启动",
  "longconn-connecting": "连接中",
  "longconn-connected": "已连接",
  "longconn-reconnecting": "重连中",
  "longconn-failed": "连接异常",
};

export function StatusPill({
  status,
  label,
  className,
}: {
  status: StatusTone;
  label?: string;
  className?: string;
}): JSX.Element {
  return (
    <span
      className={cn(
        "inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-medium",
        TONE_CLASS[status],
        className,
      )}
    >
      {label ?? TONE_LABEL[status]}
    </span>
  );
}
